import { useContext, useEffect, useRef } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { Counter } from '@/common/components/PromoBar/Counter/Counter';
import { Gray } from '@/common/constants/styles';
import { PromoBarContext } from '@/common/contexts/PromoBarContext/PromoBarContext';
import { formatCounterToStringDate } from '@/common/hooks/useCountdown/formatter/stringDateFormatter';
import useCountdown from '@/common/hooks/useCountdown/useCountdown';
import SvgIconClose from '@/common/icons/generated/common/IconClose';
import {
  SessionStorageData,
  SessionStorageKey,
  SessionStorageManager,
} from '@/common/utils/browserStorage/SessionStorageManager';
import { PromoBar as PromoBarType } from 'generatedBfw/static';

import styles from './PromoBar.module.scss';

dayjs.extend(isBetween);

interface PromoBarProps {
  promoBarData: PromoBarType | undefined;
  isFloatingOverLayout?: boolean;
}

export const promoBarStorageManager = new SessionStorageManager<
  SessionStorageData[SessionStorageKey.PROMO_BAR]
>(SessionStorageKey.PROMO_BAR);

const PromoBar = ({ promoBarData, isFloatingOverLayout = true }: PromoBarProps) => {
  const {
    isPromoBarOpen,
    handlePromoBarOpen,
    handlePromoBarFloating,
    isPromoBarScrolled,
    setPromoBarHeight,
    headerHeight,
    headerExpandedHeight,
  } = useContext(PromoBarContext);

  const barRef = useRef<HTMLDivElement | null>(null);

  const promoBarStartDate = dayjs(promoBarData?.startDate);
  const promoBarEndDate = dayjs(promoBarData?.endDate);
  const isPromoBarActive = dayjs().isBetween(promoBarStartDate, promoBarEndDate);

  useEffect(() => {
    handlePromoBarFloating(isFloatingOverLayout);

    if (!isPromoBarActive || promoBarStorageManager.getItem()) {
      handlePromoBarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPromoBarHeight(barRef.current?.offsetHeight || 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barRef.current]);

  const onEnd = () => {
    handlePromoBarOpen(false);
  };

  const handleCloseButtonClick = () => {
    handlePromoBarOpen(false);

    promoBarStorageManager.setItem(true);
  };

  const isOpen = isPromoBarActive && isPromoBarOpen;
  const isCounterVisible = promoBarData?.hasCounter;
  const initialPromoBarTopPosition = isFloatingOverLayout ? headerExpandedHeight : 0;

  const { days, hours, minutes, seconds } = formatCounterToStringDate(
    useCountdown(promoBarEndDate, { enabled: isOpen && isCounterVisible, onEnd }),
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={clsx(styles.promoBarWrapper, {
        [styles.promoBarWrapperScrolled]: isPromoBarScrolled,
        [styles.promoBarWrapperFloating]: isFloatingOverLayout,
      })}
      data-testid="promo-bar"
      ref={barRef}
      style={{
        backgroundColor: promoBarData?.backgroundColor,
        color: promoBarData?.fontColor,
        top: isPromoBarScrolled ? headerHeight : initialPromoBarTopPosition,
      }}
    >
      <div className={styles.promoTextWrapper}>
        <div className={styles.promoTextWrapperMobile}>
          <span
            dangerouslySetInnerHTML={promoBarData && { __html: promoBarData.contentMobileHtml }}
            style={{ color: promoBarData?.fontColor || Gray.x100 }}
          />
        </div>
        <div className={styles.promoTextWrapperDesktop}>
          <span
            dangerouslySetInnerHTML={promoBarData && { __html: promoBarData.contentDesktopHtml }}
            style={{ color: promoBarData?.fontColor || Gray.x100 }}
          />
        </div>
      </div>
      {isCounterVisible ? (
        <Counter days={days} hours={hours} minutes={minutes} seconds={seconds} />
      ) : null}
      <div className={styles.promoCloseIconWrapper} onClick={handleCloseButtonClick}>
        <SvgIconClose
          className={styles.promoBarCloseIcon}
          color={promoBarData?.fontColor}
          data-testid="promo-bar-close-icon"
        />
      </div>
    </div>
  );
};

export default PromoBar;
